<script setup>
import { ref, computed, watch, nextTick } from 'vue';
import { useI18n } from 'vue-i18n';
import ContactAPI from 'dashboard/api/contacts';
import TagMultiSelectComboBox from 'dashboard/components-next/combobox/TagMultiSelectComboBox.vue';
import { OnClickOutside } from '@vueuse/components';
import {
  buildCampaignAudienceTargetValue,
  DIRECT_CONTACT_AUDIENCE_TYPE,
  DIRECT_GROUP_AUDIENCE_TYPE,
  extractContactPayload,
  getImportedFileFallbackName,
  parseCampaignAudienceTargetValue,
  resolveCampaignTemplateVariables,
} from '../utils/campaignForm';

const props = defineProps({
  modelValue: {
    type: Object,
    required: true,
  },
  labels: {
    type: Array,
    default: () => [],
  },
  accountId: {
    type: [String, Number],
    default: '',
  },
  inboxId: {
    type: [String, Number],
    default: null,
  },
  isApiInbox: {
    type: Boolean,
    default: false,
  },
  availableVariables: {
    type: Array,
    default: () => [],
  },
  availableVariableDetails: {
    type: Array,
    default: () => [],
  },
});

const emit = defineEmits(['update:modelValue', 'error']);
const { t, locale } = useI18n();

const getContactFallbackText = () => {
  const translated = t('CAMPAIGN.ENHANCED.AUDIENCE.CONTACT_FALLBACK');

  if (
    translated &&
    translated !== 'CAMPAIGN.ENHANCED.AUDIENCE.CONTACT_FALLBACK'
  ) {
    return translated;
  }

  return String(locale?.value || '')
    .toLowerCase()
    .startsWith('pt')
    ? 'Contato'
    : 'Contact';
};

const updateValue = updates => {
  emit('update:modelValue', {
    ...props.modelValue,
    ...updates,
  });
};

const audienceEndpoint = computed(
  () => `/api/v1/accounts/${props.accountId}/api_campaigns/audience`
);
const showGroupsField = computed(
  () => props.isApiInbox || Boolean(props.inboxId)
);

// Contact search state
const contactSearchQuery = ref('');
const contactSearchResults = ref([]);
const selectedContactsCache = ref([]);
const isSearching = ref(false);
const groupOptions = ref([]);
const groupsLoading = ref(false);
let searchDebounceTimer = null;

const labelOptions = computed(() =>
  props.labels.map(label => ({
    value: label.id,
    label: label.title,
  }))
);

const formatContactLabel = contact => {
  const name = contact.name || '';
  const phone =
    contact.phoneNumber || contact.phone_number || contact.phone || '';
  const email = contact.email || '';
  const identifier = contact.identifier || '';
  const parts = [name, phone, email, identifier]
    .map(value => String(value || '').trim())
    .filter(Boolean);

  return (
    parts.join(', ') ||
    contact?.label ||
    `${getContactFallbackText()} #${contact.id}`
  );
};

const buildAudienceOption = (
  contact,
  audienceType = DIRECT_CONTACT_AUDIENCE_TYPE
) => {
  const rawId = contact?.id;

  if (rawId === null || rawId === undefined || rawId === '') {
    return null;
  }

  return {
    value: buildCampaignAudienceTargetValue(audienceType, rawId),
    label: formatContactLabel(contact),
    audienceType,
  };
};

const buildSelectedFallbackOption = value => {
  const parsedTarget = parseCampaignAudienceTargetValue(value);

  if (parsedTarget.type === DIRECT_GROUP_AUDIENCE_TYPE) {
    return {
      value,
      label: String(parsedTarget.id),
      audienceType: parsedTarget.type,
    };
  }

  return {
    value,
    label: `${getContactFallbackText()} #${parsedTarget.id}`,
    audienceType: parsedTarget.type,
  };
};

const isFallbackSelectedOption = (option, value) => {
  if (!option) {
    return true;
  }

  return option.label === buildSelectedFallbackOption(value).label;
};

const contactOptions = computed(() => {
  const searchResults = contactSearchResults.value.map(contact => ({
    value: contact.value,
    label: contact.label,
    audienceType: contact.audienceType,
  }));
  const cachedSelected = selectedContactsCache.value
    .filter(c => !searchResults.some(sr => sr.value === c.value))
    .map(c => ({
      value: c.value,
      label: c.label,
      audienceType: c.audienceType,
    }));
  return [...cachedSelected, ...searchResults];
});

const searchContacts = async query => {
  if (!query || query.length < 2) {
    contactSearchResults.value = [];
    return;
  }
  isSearching.value = true;
  try {
    if (showGroupsField.value && props.inboxId) {
      const [peopleResponse, groupsResponse] = await Promise.all([
        window.axios.get(audienceEndpoint.value, {
          params: {
            inbox_id: props.inboxId,
            type: 'people',
            q: query,
          },
        }),
        window.axios.get(audienceEndpoint.value, {
          params: {
            inbox_id: props.inboxId,
            type: 'groups',
            q: query,
          },
        }),
      ]);

      contactSearchResults.value = [
        ...(peopleResponse.data?.options || []).map(option =>
          buildAudienceOption(option, DIRECT_CONTACT_AUDIENCE_TYPE)
        ),
        ...(groupsResponse.data?.options || []).map(option =>
          buildAudienceOption(option, DIRECT_GROUP_AUDIENCE_TYPE)
        ),
      ].filter(Boolean);
    } else {
      const { data } = await ContactAPI.search(query, 1, 'name');
      contactSearchResults.value = (data.payload || [])
        .map(option =>
          buildAudienceOption(option, DIRECT_CONTACT_AUDIENCE_TYPE)
        )
        .filter(Boolean);
    }
  } catch {
    contactSearchResults.value = [];
  } finally {
    isSearching.value = false;
  }
};

const loadGroups = async () => {
  groupOptions.value = [];

  if (!showGroupsField.value || !props.accountId || !props.inboxId) {
    return;
  }

  groupsLoading.value = true;

  try {
    const { data } = await window.axios.get(audienceEndpoint.value, {
      params: {
        inbox_id: props.inboxId,
        type: 'groups',
      },
    });

    groupOptions.value = (data.options || []).map(group => ({
      value: group.id,
      label: group.label,
    }));
  } catch {
    groupOptions.value = [];
  } finally {
    groupsLoading.value = false;
  }
};

const onContactSearch = query => {
  contactSearchQuery.value = query;
  clearTimeout(searchDebounceTimer);
  searchDebounceTimer = setTimeout(() => searchContacts(query), 300);
};

// Cache selected contacts so their labels persist after search changes
// Fetch contacts by IDs that are not yet in cache
const fetchMissingContacts = async ids => {
  const cachedContacts = new Map(
    selectedContactsCache.value.map(contact => [contact.value, contact])
  );
  const missingContactIds = ids
    .map(value => ({ value, ...parseCampaignAudienceTargetValue(value) }))
    .filter(target => {
      if (target.type !== DIRECT_CONTACT_AUDIENCE_TYPE) {
        return false;
      }

      return isFallbackSelectedOption(
        cachedContacts.get(target.value),
        target.value
      );
    })
    .map(target => target.id);

  if (!missingContactIds.length) return;

  try {
    const results = await Promise.all(
      missingContactIds.map(id => ContactAPI.show(id).catch(() => null))
    );
    const nextCache = new Map(
      selectedContactsCache.value.map(contact => [contact.value, contact])
    );
    results.forEach(res => {
      const contact = extractContactPayload(res?.data);
      if (contact) {
        const option = buildAudienceOption(
          contact,
          DIRECT_CONTACT_AUDIENCE_TYPE
        );

        if (option) {
          nextCache.set(option.value, option);
        }
      }
    });
    selectedContactsCache.value = Array.from(nextCache.values());
  } catch {
    // Silently ignore - contacts will show as IDs
  }
};

watch(
  () => props.modelValue.contactIds,
  async newIds => {
    if (!newIds?.length) {
      selectedContactsCache.value = [];
      return;
    }
    const knownContacts = new Map(
      selectedContactsCache.value.map(contact => [contact.value, contact])
    );
    contactSearchResults.value.forEach(contact => {
      knownContacts.set(contact.value, {
        value: contact.value,
        label: contact.label,
        audienceType: contact.audienceType,
      });
    });

    selectedContactsCache.value = newIds.map(id => {
      return knownContacts.get(id) || buildSelectedFallbackOption(id);
    });

    await fetchMissingContacts(newIds);
  },
  { deep: true, immediate: true }
);

watch(
  () => [props.inboxId, props.isApiInbox],
  async ([inboxId, isApiInbox], [previousInboxId, previousIsApiInbox] = []) => {
    contactSearchQuery.value = '';
    contactSearchResults.value = [];

    if (!isApiInbox || !inboxId) {
      groupOptions.value = [];

      if ((props.modelValue.groupIds || []).length > 0) {
        updateValue({ groupIds: [] });
      }
      return;
    }

    if (
      previousIsApiInbox &&
      previousInboxId &&
      String(previousInboxId) !== String(inboxId) &&
      (props.modelValue.groupIds || []).length > 0
    ) {
      updateValue({ groupIds: [] });
    }

    await loadGroups();
  },
  { immediate: true }
);

// Contact dropdown controls
const contactDropdownOpen = ref(false);
const contactSearchInput = ref(null);

const selectedContactTags = computed(() => {
  const ids = props.modelValue.contactIds || [];
  return ids
    .map(id => selectedContactsCache.value.find(c => c.value === id))
    .filter(Boolean);
});

const isContactSelected = id => {
  return (props.modelValue.contactIds || []).includes(id);
};

const toggleContactDropdown = () => {
  contactDropdownOpen.value = !contactDropdownOpen.value;
  if (contactDropdownOpen.value) {
    nextTick(() => contactSearchInput.value?.focus());
  }
};

const toggleContact = option => {
  const currentIds = [...(props.modelValue.contactIds || [])];
  const index = currentIds.indexOf(option.value);
  if (index === -1) {
    currentIds.push(option.value);
    // Add to cache
    if (!selectedContactsCache.value.some(c => c.value === option.value)) {
      selectedContactsCache.value.push({
        value: option.value,
        label: option.label,
        audienceType: option.audienceType,
      });
    }
  } else {
    currentIds.splice(index, 1);
    selectedContactsCache.value = selectedContactsCache.value.filter(
      c => c.value !== option.value
    );
  }
  updateValue({ contactIds: currentIds });
};

const removeContact = id => {
  const currentIds = (props.modelValue.contactIds || []).filter(v => v !== id);
  selectedContactsCache.value = selectedContactsCache.value.filter(
    c => c.value !== id
  );
  updateValue({ contactIds: currentIds });
};

const importedVariables = computed(() =>
  props.availableVariableDetails.length
    ? props.availableVariableDetails
    : (
        props.availableVariables.length
          ? props.availableVariables
          : resolveCampaignTemplateVariables(props.modelValue.importedHeaders)
      ).map(variable => ({
        key: variable,
        description: '',
      }))
);
const formatVariableToken = variable => `{{${variable}}}`;
const exampleCsvPreview =
  'name,phone\nJoão Silva,5511999999999\nMaria Souza,5511888888888';
const exampleCsvHref = computed(() => {
  const csv = `${exampleCsvPreview}\n`;
  return `data:text/csv;charset=utf-8,${encodeURIComponent(csv)}`;
});

const importPreviewUrl = computed(
  () => `/api/v1/accounts/${props.accountId}/campaigns/import_audience_preview`
);

const resetImportInput = input => {
  if (input) {
    input.value = '';
  }
};

const handleImportFile = async event => {
  const input = event?.target;
  const [file] = input?.files || [];
  if (!file) return;

  const extension = file.name.split('.').pop()?.toLowerCase();
  if (!['csv', 'xls', 'xlsx'].includes(extension)) {
    emit('error', t('CAMPAIGN.ENHANCED.AUDIENCE.INVALID_FILE'));
    resetImportInput(input);
    return;
  }

  try {
    const formData = new FormData();
    formData.append('file', file);

    const { data } = await window.axios.post(importPreviewUrl.value, formData);

    updateValue({
      importFileName: file.name || getImportedFileFallbackName(),
      importedHeaders: data.headers || [],
      importedContacts: data.contacts || [],
    });
  } catch (error) {
    emit(
      'error',
      error?.response?.data?.error ||
        t('CAMPAIGN.ENHANCED.AUDIENCE.IMPORT_ERROR')
    );
    updateValue({
      importFileName: '',
      importedContacts: [],
      importedHeaders: [],
    });
  } finally {
    resetImportInput(input);
  }
};
</script>

<template>
  <div class="space-y-4">
    <div class="flex gap-3">
      <button
        type="button"
        class="inline-flex items-center justify-center h-8 px-3 text-sm font-medium rounded-lg outline-1 transition-colors"
        :class="
          modelValue.mode === 'import'
            ? 'bg-n-brand/10 text-n-blue-11 outline-n-brand'
            : 'text-n-slate-11 outline-n-strong hover:bg-n-alpha-2'
        "
        @click="updateValue({ mode: 'import' })"
      >
        {{ t('CAMPAIGN.ENHANCED.AUDIENCE.IMPORT_SPREADSHEET') }}
      </button>
      <button
        type="button"
        class="inline-flex items-center justify-center h-8 px-3 text-sm font-medium rounded-lg outline-1 transition-colors"
        :class="
          modelValue.mode === 'existing'
            ? 'bg-n-brand/10 text-n-blue-11 outline-n-brand'
            : 'text-n-slate-11 outline-n-strong hover:bg-n-alpha-2'
        "
        @click="updateValue({ mode: 'existing' })"
      >
        {{ t('CAMPAIGN.ENHANCED.AUDIENCE.LABELS_AND_CONTACTS') }}
      </button>
    </div>

    <div v-if="modelValue.mode === 'import'" class="space-y-3">
      <div class="rounded-lg border border-n-weak p-4">
        <label class="block mb-2 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.ENHANCED.AUDIENCE.IMPORT_LABEL') }}
        </label>
        <input
          type="file"
          accept=".csv,.xls,.xlsx"
          class="block w-full text-sm text-n-slate-11 file:mr-4 file:py-2 file:px-4 file:rounded-lg file:border-0 file:text-sm file:font-medium file:bg-n-blue-9 file:text-white"
          @change="handleImportFile"
        />
        <p class="mt-2 text-xs text-n-slate-11">
          {{ t('CAMPAIGN.ENHANCED.AUDIENCE.IMPORT_HELP') }}
        </p>
        <div
          class="mt-3 rounded-lg bg-n-alpha-black2 p-3 text-xs text-n-slate-12"
        >
          <p class="font-medium">
            {{ t('CAMPAIGN.ENHANCED.AUDIENCE.EXAMPLE_TITLE') }}
          </p>
          <pre class="mt-2 whitespace-pre-wrap">{{ exampleCsvPreview }}</pre>
          <a
            class="inline-flex mt-2 text-n-blue-11 hover:underline"
            :href="exampleCsvHref"
            download="campaign-audience-example.csv"
          >
            {{ t('CAMPAIGN.ENHANCED.AUDIENCE.DOWNLOAD_EXAMPLE') }}
          </a>
        </div>
      </div>

      <div
        v-if="modelValue.importFileName"
        class="rounded-lg border border-n-weak p-4"
      >
        <p class="text-sm text-n-slate-12">
          {{ t('CAMPAIGN.ENHANCED.AUDIENCE.FILE_LABEL') }}
          <strong>{{ modelValue.importFileName }}</strong>
        </p>
        <p class="mt-1 text-sm text-n-slate-11">
          {{
            t('CAMPAIGN.ENHANCED.AUDIENCE.IMPORTED_COUNT', {
              count: modelValue.importedContacts.length,
            })
          }}
        </p>
      </div>

      <div
        v-if="importedVariables.length"
        class="rounded-lg border border-n-weak p-4"
      >
        <p class="mb-2 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.ENHANCED.AUDIENCE.AVAILABLE_VARIABLES') }}
        </p>
        <div class="flex flex-wrap gap-2">
          <span
            v-for="variable in importedVariables"
            :key="variable.key"
            v-tooltip.top="variable.description"
            :title="variable.description"
            class="px-2 py-1 text-xs rounded-lg bg-n-alpha-black2 text-n-slate-12"
          >
            {{ formatVariableToken(variable.key) }}
          </span>
        </div>
      </div>
    </div>

    <div v-else class="space-y-4">
      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.ENHANCED.AUDIENCE.LABELS_LABEL') }}
        </label>
        <TagMultiSelectComboBox
          :model-value="modelValue.labelIds"
          :options="labelOptions"
          :placeholder="t('CAMPAIGN.ENHANCED.AUDIENCE.SELECT_LABELS')"
          @update:model-value="updateValue({ labelIds: $event })"
        />
      </div>

      <div class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.ENHANCED.AUDIENCE.CONTACTS_LABEL') }}
        </label>
        <OnClickOutside @trigger="contactDropdownOpen = false">
          <div class="relative w-full min-w-0 group/combobox">
            <div
              class="flex flex-wrap w-full gap-2 px-3 py-2.5 border rounded-lg cursor-pointer bg-n-alpha-black2 min-h-[42px] transition-all duration-500 ease-in-out"
              :class="{
                'border-n-weak dark:border-n-weak hover:border-n-slate-6 dark:hover:border-n-slate-6':
                  !contactDropdownOpen,
                'border-n-brand': contactDropdownOpen,
              }"
              @click="toggleContactDropdown"
            >
              <div
                v-for="tag in selectedContactTags"
                :key="tag.value"
                class="flex items-center justify-center max-w-full gap-1 px-2 py-0.5 rounded-lg bg-n-alpha-black1"
                @click.stop
              >
                <span
                  class="flex-grow min-w-0 text-sm truncate text-n-slate-12"
                >
                  {{ tag.label }}
                </span>
                <span
                  class="flex-shrink-0 cursor-pointer i-lucide-x size-3 text-n-slate-11"
                  @click="removeContact(tag.value)"
                />
              </div>
              <span
                v-if="!selectedContactTags.length"
                class="flex items-center text-sm text-n-slate-11"
              >
                {{ t('CAMPAIGN.ENHANCED.AUDIENCE.SELECT_CONTACTS') }}
              </span>
            </div>
            <div
              v-show="contactDropdownOpen"
              class="absolute z-50 w-full mt-1 border rounded-md shadow-lg bg-n-solid-1 border-n-strong"
            >
              <div class="relative border-b border-n-strong">
                <span class="absolute i-lucide-search top-2.5 size-4 left-3" />
                <input
                  ref="contactSearchInput"
                  v-model="contactSearchQuery"
                  type="search"
                  :placeholder="t('CAMPAIGN.ENHANCED.AUDIENCE.SEARCH_CONTACTS')"
                  class="reset-base w-full py-2 pl-10 pr-2 text-sm focus:outline-none border-none rounded-t-md bg-n-solid-1 text-n-slate-12"
                  @input="onContactSearch($event.target.value)"
                />
              </div>
              <ul class="py-1 mb-0 overflow-auto max-h-60" role="listbox">
                <li
                  v-if="isSearching"
                  class="px-3 py-2 text-sm text-n-slate-11"
                >
                  {{ t('CAMPAIGN.ENHANCED.AUDIENCE.SEARCHING') }}
                </li>
                <li
                  v-else-if="
                    contactSearchQuery.length > 0 &&
                    contactSearchQuery.length < 2
                  "
                  class="px-3 py-2 text-sm text-n-slate-11"
                >
                  {{ t('CAMPAIGN.ENHANCED.AUDIENCE.TYPE_TO_SEARCH') }}
                </li>
                <li
                  v-else-if="
                    contactOptions.length === 0 &&
                    contactSearchQuery.length >= 2
                  "
                  class="px-3 py-2 text-sm text-n-slate-11"
                >
                  {{ t('CAMPAIGN.ENHANCED.AUDIENCE.NO_RESULTS') }}
                </li>
                <template v-else>
                  <li
                    v-for="option in contactOptions"
                    :key="option.value"
                    class="flex items-center justify-between w-full gap-2 px-3 py-2 text-sm transition-colors duration-150 cursor-pointer hover:bg-n-alpha-2"
                    :class="{
                      'bg-n-alpha-2': isContactSelected(option.value),
                    }"
                    role="option"
                    :aria-selected="isContactSelected(option.value)"
                    @click="toggleContact(option)"
                  >
                    <span
                      :class="{
                        'font-medium': isContactSelected(option.value),
                      }"
                      class="text-n-slate-12"
                    >
                      {{ option.label }}
                    </span>
                    <span
                      v-if="isContactSelected(option.value)"
                      class="flex-shrink-0 i-lucide-check size-4 text-n-slate-11"
                    />
                  </li>
                </template>
                <li
                  v-if="
                    !isSearching &&
                    contactSearchQuery.length === 0 &&
                    contactOptions.length === 0
                  "
                  class="px-3 py-2 text-sm text-n-slate-11"
                >
                  {{ t('CAMPAIGN.ENHANCED.AUDIENCE.TYPE_TO_SEARCH') }}
                </li>
              </ul>
            </div>
          </div>
        </OnClickOutside>
      </div>

      <div v-if="showGroupsField" class="flex flex-col gap-1">
        <label class="mb-0.5 text-sm font-medium text-n-slate-12">
          {{ t('CAMPAIGN.ENHANCED.AUDIENCE.GROUPS_LABEL') }}
        </label>
        <TagMultiSelectComboBox
          :model-value="modelValue.groupIds"
          :options="groupOptions"
          :placeholder="
            groupsLoading
              ? t('CAMPAIGN.ENHANCED.AUDIENCE.LOADING_GROUPS')
              : t('CAMPAIGN.ENHANCED.AUDIENCE.SELECT_GROUPS')
          "
          :disabled="!inboxId || groupsLoading"
          @update:model-value="updateValue({ groupIds: $event })"
        />
        <p class="text-xs text-n-slate-11">
          {{ t('CAMPAIGN.ENHANCED.AUDIENCE.GROUPS_HELP') }}
        </p>
      </div>

      <p class="text-xs text-n-slate-11">
        {{ t('CAMPAIGN.ENHANCED.AUDIENCE.DEFAULT_VARIABLES_HINT') }}
      </p>
    </div>
  </div>
</template>
